import React, { useState } from 'react';
import {
    ArrowLeft, DollarSign, Lock, Loader2, Sparkles, AlertTriangle,
    Phone, Briefcase, ChevronRight, Copy, Check, ListChecks
} from 'lucide-react';
import { generateAIContent, getPrompts } from '../aiService.js';
import { useSubscription } from '../contexts/SubscriptionContext.jsx';

// Salary Negotiator — career-ops has "negotiation frameworks + scripts"
// as a first-class concept; Architect users have zero negotiation tooling
// today. Most candidates leave $10–50K on the table because they don't know
// what to say. This is a $3 unlock that pairs naturally with the per-feature
// payment model.
const SalaryNegotiator = ({ resumeData, apiConfig, onBack, showNotification }) => {
    const { hasFeature, purchaseFeature, getFeaturePrice, isPremium } = useSubscription();
    const isUnlocked = isPremium || hasFeature('salary_negotiation');
    const price = getFeaturePrice('salary_negotiation') || 3;

    const [form, setForm] = useState({
        targetRole: '',
        offer: '',
        currentSalary: '',
        targetSalary: '',
        leverage: '',
        walkAway: '',
        location: '',
        yearsExperience: ''
    });
    const [loading, setLoading] = useState(false);
    const [playbook, setPlaybook] = useState(null);
    const [copied, setCopied] = useState(null);

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const handleGenerate = async () => {
        if (!form.targetRole.trim()) {
            showNotification?.('Add the target role first.');
            return;
        }
        const hasAi = apiConfig && (apiConfig.gemini || apiConfig.openai || apiConfig.anthropic || apiConfig.groq || apiConfig.deepseek);
        if (!hasAi) {
            showNotification?.('Configure an AI provider in AI Settings first.');
            return;
        }
        setLoading(true);
        setPlaybook(null);
        try {
            const prompt = getPrompts('salary-negotiation', { ...form, resume: resumeData });
            const raw = await generateAIContent(apiConfig, prompt);
            const json = raw.replace(/```json/g, '').replace(/```/g, '').trim();
            const parsed = JSON.parse(json);
            setPlaybook(parsed);
        } catch (e) {
            console.error('Negotiation generate error:', e);
            showNotification?.(`Failed to generate playbook: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const copy = async (text, id) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(id);
            setTimeout(() => setCopied(null), 1500);
        } catch { /* clipboard not available */ }
    };

    if (!isUnlocked) {
        return (
            <div className="min-h-screen bg-slate-50 p-6">
                <button onClick={onBack} className="text-slate-500 hover:text-slate-700 mb-6 flex items-center gap-2">
                    <ArrowLeft size={16} /> Back
                </button>
                <div className="max-w-md mx-auto text-center bg-white rounded-2xl shadow-lg p-8">
                    <div className="bg-emerald-100 p-4 rounded-full text-emerald-600 inline-block mb-4">
                        <DollarSign size={48} />
                    </div>
                    <h2 className="text-2xl font-bold text-slate-800 mb-2">Salary Negotiator</h2>
                    <p className="text-slate-600 mb-6">
                        Most candidates leave $10–50K on the table because they don't know what to say. Get a negotiation playbook tailored to your role, leverage, and offer.
                    </p>
                    <ul className="text-left space-y-2 mb-6 text-sm text-slate-700">
                        <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Recruiter call script (the first one matters most)</li>
                        <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Hiring-manager script</li>
                        <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> 3-level counter-offer ladder</li>
                        <li className="flex items-center gap-2"><Check size={14} className="text-emerald-500" /> Red flags + walk-away signals</li>
                    </ul>
                    <button
                        onClick={() => purchaseFeature('salary_negotiation', price)}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2 transition-transform active:scale-95"
                    >
                        <Lock size={16} /> Unlock for ${price}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="bg-white border-b border-slate-200 px-6 py-4 flex items-center gap-4">
                <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-lg">
                    <ArrowLeft size={20} />
                </button>
                <div className="flex items-center gap-2">
                    <DollarSign className="text-emerald-600" size={20} />
                    <h1 className="font-bold text-slate-800">Salary Negotiator</h1>
                </div>
            </header>

            <div className="max-w-6xl mx-auto p-6 grid md:grid-cols-2 gap-6">
                {/* Inputs */}
                <div className="space-y-4">
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Your situation</p>
                        <Field label="Role you're negotiating" value={form.targetRole} onChange={v => set('targetRole', v)} placeholder="Senior Frontend Engineer" />
                        <Field label="Years of experience" value={form.yearsExperience} onChange={v => set('yearsExperience', v)} placeholder="8" />
                        <Field label="Location / market" value={form.location} onChange={v => set('location', v)} placeholder="San Francisco / NYC / Remote-US" />
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Numbers</p>
                        <Field label="Current / prior salary (optional)" value={form.currentSalary} onChange={v => set('currentSalary', v)} placeholder="$160K base + $20K bonus" />
                        <Field label="Your target" value={form.targetSalary} onChange={v => set('targetSalary', v)} placeholder="$220K base" />
                        <Field label="Offer on the table (if any)" value={form.offer} onChange={v => set('offer', v)} placeholder="$180K base, $40K signing, no equity" multiline />
                        <Field label="Walk-away point" value={form.walkAway} onChange={v => set('walkAway', v)} placeholder="$190K base — below that I take the other offer" />
                    </div>
                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5 space-y-3">
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-wider">Leverage</p>
                        <Field
                            label="What's your leverage?"
                            value={form.leverage}
                            onChange={v => set('leverage', v)}
                            placeholder="Competing offer from Stripe at $210K base, plus my current employer is happy to retain"
                            multiline
                        />
                    </div>
                    <button
                        onClick={handleGenerate}
                        disabled={loading || !form.targetRole.trim()}
                        className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-bold py-3 rounded-lg flex items-center justify-center gap-2"
                    >
                        {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
                        {loading ? 'Generating playbook…' : 'Generate Playbook'}
                    </button>
                </div>

                {/* Output */}
                <div className="space-y-4">
                    {!playbook && !loading && (
                        <div className="bg-white rounded-xl border border-dashed border-slate-300 p-8 text-center text-slate-500">
                            <DollarSign size={36} className="mx-auto mb-3 text-slate-300" />
                            <p className="text-sm">Fill in your situation on the left and click "Generate Playbook".</p>
                            <p className="text-xs mt-2 text-slate-400">The more honest detail you provide (especially leverage + walk-away), the more useful the scripts.</p>
                        </div>
                    )}
                    {playbook && (
                        <>
                            <ScriptCard
                                title="Recruiter call script"
                                icon={Phone}
                                content={playbook.recruiterScript}
                                copyId="recruiter"
                                copied={copied}
                                onCopy={copy}
                                color="blue"
                            />
                            <ScriptCard
                                title="Hiring-manager script"
                                icon={Briefcase}
                                content={playbook.hiringManagerScript}
                                copyId="hm"
                                copied={copied}
                                onCopy={copy}
                                color="purple"
                            />
                            {playbook.counterOfferLadder?.length > 0 && (
                                <div className="bg-white rounded-xl border border-emerald-200 p-5">
                                    <p className="text-sm font-bold text-emerald-700 mb-3 flex items-center gap-2">
                                        <ChevronRight size={16} /> Counter-offer ladder
                                    </p>
                                    <div className="space-y-3">
                                        {playbook.counterOfferLadder.map((step, i) => (
                                            <div key={i} className="border border-slate-200 rounded-lg p-3">
                                                <p className="text-xs font-bold uppercase text-emerald-600 mb-1">{step.level}</p>
                                                <p className="text-sm text-slate-800 mb-1">{step.ask}</p>
                                                <p className="text-xs text-slate-500 italic">{step.rationale}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {playbook.redFlags?.length > 0 && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                                    <p className="text-sm font-bold text-amber-800 mb-2 flex items-center gap-2">
                                        <AlertTriangle size={16} /> Red flags — if you hear these, walk
                                    </p>
                                    <ul className="text-sm text-amber-900 space-y-1">
                                        {playbook.redFlags.map((rf, i) => <li key={i}>• {rf}</li>)}
                                    </ul>
                                </div>
                            )}
                            {playbook.topThreeMoves?.length > 0 && (
                                <div className="bg-slate-900 text-white rounded-xl p-5">
                                    <p className="text-sm font-bold mb-2 flex items-center gap-2">
                                        <ListChecks size={16} /> Do these three things this week
                                    </p>
                                    <ol className="text-sm space-y-1 list-decimal list-inside">
                                        {playbook.topThreeMoves.map((m, i) => <li key={i}>{m}</li>)}
                                    </ol>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

const Field = ({ label, value, onChange, placeholder, multiline }) => (
    <div>
        <label className="text-xs font-medium text-slate-600 mb-1 block">{label}</label>
        {multiline ? (
            <textarea
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                rows={2}
                className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:ring-2 focus:ring-emerald-500 outline-none resize-none"
            />
        ) : (
            <input
                type="text"
                value={value}
                onChange={e => onChange(e.target.value)}
                placeholder={placeholder}
                className="w-full text-sm border border-slate-200 rounded-lg p-2 focus:ring-2 focus:ring-emerald-500 outline-none"
            />
        )}
    </div>
);

const ScriptCard = ({ title, icon: Icon, content, copyId, copied, onCopy, color }) => {
    const colorMap = {
        blue: 'border-blue-200 bg-blue-50 text-blue-800',
        purple: 'border-purple-200 bg-purple-50 text-purple-800'
    };
    return (
        <div className={`rounded-xl border ${colorMap[color]} p-5`}>
            <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-bold flex items-center gap-2">
                    <Icon size={16} /> {title}
                </p>
                <button onClick={() => onCopy(content, copyId)} className="text-xs hover:underline flex items-center gap-1">
                    {copied === copyId ? <Check size={12} /> : <Copy size={12} />}
                    {copied === copyId ? 'Copied' : 'Copy'}
                </button>
            </div>
            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">"{content}"</p>
        </div>
    );
};

export default SalaryNegotiator;
